import type { AlertColor } from '@mui/material/Alert';
import type { SxProps, Theme } from '@mui/material/styles';
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponseHeaders,
  InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from '../../types/api';
import type {
  ActivityData,
  ActivityLogEntry,
  ActivityMetadata,
  ActivityType,
} from '../activityService';

interface AxiosErrorWithResponse extends AxiosError {
  response: {
    status: number;
    data: {
      message?: string;
    };
    statusText: string;
    headers: AxiosResponseHeaders;
    config: InternalAxiosRequestConfig;
  };
}

// Callback types
type SnackbarCallback = (
  message: string,
  severity: AlertColor,
  duration?: number,
  customStyle?: SxProps<Theme> | null
) => void;
type LogoutCallback = () => void;

interface ActivityLogger {
  logUserActivity: (
    activityType: ActivityType,
    data: ActivityData,
    metadata?: ActivityMetadata
  ) => Promise<ApiResponse<ActivityLogEntry>>;
  ACTIVITY_TYPES: Record<string, ActivityType>;
}

// Global callbacks
let showSnackbarCallback: SnackbarCallback | null = null;
let logoutCallback: LogoutCallback | null = null;
let isLoggingOut = false;

export const registerSnackbarCallback = (callback: SnackbarCallback | null): void => {
  showSnackbarCallback = callback;
};

export const registerLogoutCallback = (callback: LogoutCallback | null): void => {
  logoutCallback = callback;
};

export const resetLogoutFlag = (): void => {
  isLoggingOut = false;
};

export const setupResponseInterceptors = (
  client: AxiosInstance,
  activityLogger: ActivityLogger
): void => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const axiosError = error as AxiosErrorWithResponse;

      // Check if this is a 403 error with a specific limit message
      let isLimitError = false;
      let limitMessage = '';

      // Check for 429 (Too Many Requests) - Always a limit error
      if (axiosError.response?.status === 429) {
        isLimitError = true;
        const data = axiosError.response.data;
        limitMessage =
          (typeof data === 'object' && 'message' in data && typeof data.message === 'string'
            ? data.message
            : '') ||
          (typeof data === 'object' && 'error' in data && typeof data.error === 'string'
            ? data.error
            : '') ||
          'Too many requests. Please try again later.';
      }
      // Check for 403 (Forbidden) or 400 (Bad Request - typically subscription validation) with specific limit messages
      else if (
        (axiosError.response?.status === 403 || axiosError.response?.status === 400) &&
        axiosError.response?.data
      ) {
        const data = axiosError.response.data;
        // Check both 'message' and 'error' fields
        const msg =
          (typeof data === 'object' && 'message' in data && typeof data.message === 'string'
            ? data.message
            : '') ||
          (typeof data === 'object' && 'error' in data && typeof data.error === 'string'
            ? data.error
            : '') ||
          '';

        if (msg) {
          const lowerMsg = msg.toLowerCase();
          if (
            lowerMsg.includes('monthly limit') ||
            lowerMsg.includes('billing period') ||
            lowerMsg.includes('subscription') ||
            lowerMsg.includes('renew') ||
            lowerMsg.includes('payment') ||
            lowerMsg.includes('limit reached') ||
            lowerMsg.includes('exceeded your limit')
          ) {
            isLimitError = true;
            limitMessage = msg;
          }
        }
      }

      // Handle limit errors separately
      if (isLimitError && limitMessage) {
        if (showSnackbarCallback) {
          const limitExceededMessage = `⚠️ LIMIT EXCEEDED: ${limitMessage}`;
          showSnackbarCallback(limitExceededMessage, 'warning' as AlertColor, 5000);
        }
      }

      // Handle token expiration (401), but NOT limit errors
      if (axiosError.response?.status === 401 && !isLimitError) {
        if (!isLoggingOut) {
          isLoggingOut = true;

          try {
            // Log security event before logout
            await activityLogger.logUserActivity(
              activityLogger.ACTIVITY_TYPES.SECURITY_EVENT ?? ('security_event' as ActivityType),
              {
                event: 'permission_denied_logout',
                requestUrl: axiosError.config?.url,
                requestMethod: axiosError.config?.method,
                responseStatus: axiosError.response.status,
                errorMessage:
                  axiosError.response?.data &&
                  typeof axiosError.response.data === 'object' &&
                  'message' in axiosError.response.data
                    ? (axiosError.response.data.message as string)
                    : 'No error message provided.',
              },
              {
                reason: `Automatic logout due to ${axiosError.response.status} response on endpoint: ${axiosError.config?.url}`,
              }
            );
          } catch (_loggingError: unknown) {
            // Silent failure for logging
          }

          if (logoutCallback) {
            sessionStorage.setItem(
              'logoutReason',
              'Your session has expired or permissions have changed. Please log in again for security reasons.'
            );
            logoutCallback();
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

// Export callback functions for backward compatibility
export { isLoggingOut, logoutCallback, showSnackbarCallback };
